// ══════════════════════════════════════════════════════════════════════════════
// POST COMPOSER - Créateur de post style Workplace by Meta (Version avancée)
// Supporte: Texte, Sondages, Événements, Tâches assignées, Documents avec preview
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Image, Video, BarChart3, Calendar, X, Loader2, Plus, Trash2, MapPin, Clock,
  CheckSquare, FileText, Users, Flag, AlertCircle, ChevronDown, Paperclip
} from 'lucide-react';
import { Button } from '../ui/button';

// Priorités de tâches
const TASK_PRIORITIES = [
  { id: 'low', label: 'Basse', color: '#22c55e', icon: '🟢' },
  { id: 'medium', label: 'Moyenne', color: '#f59e0b', icon: '🟡' },
  { id: 'high', label: 'Haute', color: '#ef4444', icon: '🔴' },
  { id: 'urgent', label: 'Urgente', color: '#dc2626', icon: '🚨' },
];

// Extensions de documents supportées
const DOCUMENT_EXTENSIONS = {
  pdf: { icon: '📄', color: '#ef4444', label: 'PDF' },
  doc: { icon: '📝', color: '#2563eb', label: 'Word' },
  docx: { icon: '📝', color: '#2563eb', label: 'Word' },
  xls: { icon: '📊', color: '#22c55e', label: 'Excel' },
  xlsx: { icon: '📊', color: '#22c55e', label: 'Excel' },
  ppt: { icon: '📽️', color: '#f97316', label: 'PowerPoint' },
  pptx: { icon: '📽️', color: '#f97316', label: 'PowerPoint' },
  txt: { icon: '📃', color: '#6b7280', label: 'Texte' },
  csv: { icon: '📈', color: '#10b981', label: 'CSV' },
  zip: { icon: '📦', color: '#8b5cf6', label: 'Archive' },
};

export default function PostComposer({ onSubmit, members = [], currentUser, accentColor = '#E8650A' }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('text');
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Poll
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollMultiple, setPollMultiple] = useState(false);
  
  // Event
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  
  // Task
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskAssignees, setTaskAssignees] = useState([]);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);

  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files).slice(0, 10 - files.length);
    setFiles([...files, ...selected]);
    selected.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreviews(prev => [...prev, { type: file.type.startsWith('image/') ? 'image' : 'video', url: ev.target.result }]);
      reader.readAsDataURL(file);
    });
  };
  
  const handleDocSelect = (e) => {
    const selected = Array.from(e.target.files).slice(0, 5 - documents.length);
    const newDocs = selected.map(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const docInfo = DOCUMENT_EXTENSIONS[ext] || { icon: '📁', color: '#6b7280', label: 'Fichier' };
      return {
        file,
        name: file.name,
        size: file.size,
        ext,
        ...docInfo
      };
    });
    setDocuments([...documents, ...newDocs]);
  };

  const removeFile = (i) => {
    setFiles(files.filter((_, idx) => idx !== i));
    setFilePreviews(filePreviews.filter((_, idx) => idx !== i));
  };
  
  const removeDocument = (i) => {
    setDocuments(documents.filter((_, idx) => idx !== i));
  };
  
  const toggleAssignee = (member) => {
    if (taskAssignees.find(a => a.id === member.user_id || a.id === member.id)) {
      setTaskAssignees(taskAssignees.filter(a => a.id !== member.user_id && a.id !== member.id));
    } else {
      setTaskAssignees([...taskAssignees, { 
        id: member.user_id || member.id, 
        name: member.user?.name || member.name || 'Membre'
      }]);
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const resetForm = () => {
    setContent('');
    setPostType('text');
    setFiles([]);
    setFilePreviews([]);
    setDocuments([]);
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollMultiple(false);
    setEventTitle('');
    setEventDate('');
    setEventLocation('');
    setTaskTitle('');
    setTaskDescription('');
    setTaskDueDate('');
    setTaskPriority('medium');
    setTaskAssignees([]);
    setIsExpanded(false);
  };

  const handleSubmit = async () => {
    if (postType === 'text' && !content.trim() && files.length === 0 && documents.length === 0) return;
    if (postType === 'poll' && (!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2)) return;
    if (postType === 'event' && !eventTitle.trim()) return;
    if (postType === 'task' && !taskTitle.trim()) return;

    setIsSubmitting(true);
    const success = await onSubmit({
      content,
      type: postType,
      files,
      documents: documents.map(d => d.file),
      mentions: [],
      poll: postType === 'poll' ? { question: pollQuestion, options: pollOptions.filter(o => o.trim()), multiple: pollMultiple } : null,
      event: postType === 'event' ? { title: eventTitle, date: eventDate, location: eventLocation } : null,
      task: postType === 'task' ? {
        title: taskTitle,
        description: taskDescription,
        due_date: taskDueDate,
        priority: taskPriority,
        assignees: taskAssignees
      } : null
    });
    if (success) resetForm();
    setIsSubmitting(false);
  };

  if (!isExpanded) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:bg-gray-50" onClick={() => setIsExpanded(true)} data-testid="post-composer-collapsed">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: accentColor }}>
            {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 px-4 py-3 bg-gray-100 rounded-full text-gray-500 text-sm">
            Quoi de neuf, {currentUser?.name?.split(' ')[0] || 'toi'} ?
          </div>
        </div>
        <div className="flex items-center justify-around mt-3 pt-3 border-t border-gray-100">
          <button className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <Image size={20} className="text-green-500" /> <span className="text-sm">Photo</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <CheckSquare size={20} className="text-orange-500" /> <span className="text-sm">Tâche</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <FileText size={20} className="text-blue-500" /> <span className="text-sm">Document</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: accentColor }}>
            {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <p className="font-semibold text-gray-900">{currentUser?.name || 'Utilisateur'}</p>
        </div>
        <button onClick={() => setIsExpanded(false)} className="p-2 hover:bg-gray-100 rounded-full">
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Type selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: 'text', label: 'Publication', color: accentColor },
          { id: 'task', label: 'Tâche', color: '#F97316', icon: CheckSquare },
          { id: 'poll', label: 'Sondage', color: '#8B5CF6', icon: BarChart3 },
          { id: 'event', label: 'Événement', color: '#EC4899', icon: Calendar },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setPostType(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              postType === t.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={postType === t.id ? { backgroundColor: t.color } : {}}
            data-testid={`post-type-${t.id}`}
          >
            {t.icon && <t.icon size={16} />} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={postType === 'poll' ? 'Contexte du sondage (optionnel)...' : postType === 'event' ? 'Description (optionnel)...' : 'Quoi de neuf ?'}
        rows={3}
        className="w-full p-3 text-gray-800 placeholder-gray-400 resize-none focus:outline-none text-lg border-0"
      />

      {/* Poll form */}
      {postType === 'poll' && (
        <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
          <input
            type="text"
            value={pollQuestion}
            onChange={(e) => setPollQuestion(e.target.value)}
            placeholder="Ta question *"
            className="w-full px-4 py-3 bg-white border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 mb-3"
          />
          <div className="space-y-2">
            {pollOptions.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => { const n = [...pollOptions]; n[i] = e.target.value; setPollOptions(n); }}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 px-4 py-2 bg-white border border-purple-200 rounded-lg"
                />
                {pollOptions.length > 2 && (
                  <button onClick={() => setPollOptions(pollOptions.filter((_, idx) => idx !== i))} className="p-2 text-red-500">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {pollOptions.length < 10 && (
            <button onClick={() => setPollOptions([...pollOptions, ''])} className="flex items-center gap-2 mt-2 px-3 py-2 text-purple-600 hover:bg-purple-100 rounded-lg text-sm">
              <Plus size={16} /> Ajouter une option
            </button>
          )}
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <input type="checkbox" checked={pollMultiple} onChange={(e) => setPollMultiple(e.target.checked)} className="rounded" />
            Choix multiples
          </label>
        </div>
      )}

      {/* Event form */}
      {postType === 'event' && (
        <div className="mt-4 p-4 bg-pink-50 rounded-xl border border-pink-200">
          <input
            type="text"
            value={eventTitle}
            onChange={(e) => setEventTitle(e.target.value)}
            placeholder="Titre de l'événement *"
            className="w-full px-4 py-3 bg-white border border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-500 mb-3"
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-pink-500" />
              <input type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="flex-1 px-3 py-2 bg-white border border-pink-200 rounded-lg" />
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-pink-500" />
              <input type="text" value={eventLocation} onChange={(e) => setEventLocation(e.target.value)} placeholder="Lieu (optionnel)" className="flex-1 px-3 py-2 bg-white border border-pink-200 rounded-lg" />
            </div>
          </div>
        </div>
      )}
      
      {/* Task form */}
      {postType === 'task' && (
        <div className="mt-4 p-4 bg-orange-50 rounded-xl border border-orange-200" data-testid="task-form">
          <div className="flex items-center gap-2 mb-3">
            <CheckSquare size={18} className="text-orange-500" />
            <span className="font-medium text-orange-700">Nouvelle tâche</span>
          </div>
          
          <input
            type="text"
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            placeholder="Titre de la tâche *"
            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 mb-3"
            data-testid="task-title-input"
          />
          
          <textarea
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            placeholder="Description (optionnel)..."
            rows={2}
            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 mb-3 resize-none"
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {/* Date d'échéance */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date d'échéance</label>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-orange-500" />
                <input 
                  type="datetime-local" 
                  value={taskDueDate} 
                  onChange={(e) => setTaskDueDate(e.target.value)} 
                  className="flex-1 px-3 py-2 bg-white border border-orange-200 rounded-lg text-sm"
                />
              </div>
            </div>
            
            {/* Priorité */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priorité</label>
              <div className="flex gap-1">
                {TASK_PRIORITIES.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setTaskPriority(p.id)}
                    className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                      taskPriority === p.id 
                        ? 'text-white' 
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    style={taskPriority === p.id ? { backgroundColor: p.color, borderColor: p.color } : { borderColor: '#e5e7eb' }}
                  >
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Assignation */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Assigner à</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-orange-200 rounded-lg text-left"
                data-testid="assignee-dropdown-btn"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Users size={16} className="text-orange-500" />
                  {taskAssignees.length === 0 ? (
                    <span className="text-gray-400 text-sm">Sélectionner des membres...</span>
                  ) : (
                    taskAssignees.map(a => (
                      <span key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                        {a.name}
                        <button 
                          onClick={(e) => { e.stopPropagation(); toggleAssignee({ id: a.id, name: a.name }); }}
                          className="hover:text-orange-900"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${showAssigneeDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown des membres */}
              <AnimatePresence>
                {showAssigneeDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto"
                  >
                    {members.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        Aucun membre dans l'équipe
                      </div>
                    ) : (
                      members.map(member => {
                        const isSelected = taskAssignees.find(a => a.id === member.user_id || a.id === member.id);
                        return (
                          <button
                            key={member.user_id || member.id}
                            onClick={() => toggleAssignee(member)}
                            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-orange-50' : ''}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-sm font-bold">
                              {(member.user?.name || member.name || '?').charAt(0).toUpperCase()}
                            </div>
                            <span className="flex-1 text-left text-sm font-medium text-gray-700">
                              {member.user?.name || member.name || 'Membre'}
                            </span>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                                <CheckSquare size={12} className="text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}

      {/* File previews */}
      {filePreviews.length > 0 && (
        <div className="mt-4 grid grid-cols-4 gap-2">
          {filePreviews.map((p, i) => (
            <div key={i} className="relative group">
              <img src={p.url} alt="" className="w-full h-20 object-cover rounded-lg" />
              <button onClick={() => removeFile(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Document previews */}
      {documents.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="document-previews">
          {documents.map((doc, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 group">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                style={{ backgroundColor: `${doc.color}15` }}
              >
                {doc.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate">{doc.name}</p>
                <p className="text-xs text-gray-500">{doc.label} • {formatFileSize(doc.size)}</p>
              </div>
              <button 
                onClick={() => removeDocument(i)} 
                className="p-2 hover:bg-red-100 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Hidden file inputs */}
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*,video/*" multiple className="hidden" />
      <input type="file" ref={docInputRef} onChange={handleDocSelect} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple className="hidden" />

      {/* Actions */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-1">
          <button onClick={() => fileInputRef.current?.click()} className="p-2 hover:bg-gray-100 rounded-full text-green-500" title="Photo/Vidéo">
            <Image size={22} />
          </button>
          <button onClick={() => docInputRef.current?.click()} className="p-2 hover:bg-gray-100 rounded-full text-blue-500" title="Document">
            <Paperclip size={22} />
          </button>
          <button onClick={() => setPostType('poll')} className={`p-2 hover:bg-gray-100 rounded-full ${postType === 'poll' ? 'text-purple-500 bg-purple-50' : 'text-purple-400'}`} title="Sondage">
            <BarChart3 size={22} />
          </button>
          <button onClick={() => setPostType('event')} className={`p-2 hover:bg-gray-100 rounded-full ${postType === 'event' ? 'text-pink-500 bg-pink-50' : 'text-pink-400'}`} title="Événement">
            <Calendar size={22} />
          </button>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || (postType === 'text' && !content.trim() && files.length === 0 && documents.length === 0) || (postType === 'task' && !taskTitle.trim())}
          className="px-6 text-white"
          style={{ backgroundColor: accentColor }}
          data-testid="submit-post-btn"
        >
          {isSubmitting ? <><Loader2 size={18} className="mr-2 animate-spin" /> Publication...</> : 'Publier'}
        </Button>
      </div>
    </div>
  );
}
